import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api-new';

interface User {
  id: number;
  email: string;
  name: string;
  role: 'user' | 'admin' | 'doctor';
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  loading: boolean;
}

interface RegisterData {
  email: string;
  password: string;
  name: string;
  phone?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      // Ensure API client has the token
      api.setToken(token);

      // If it's a demo token, we can just set the user directly without calling the API
      // This avoids 401s if the backend check fails for some reason, and speeds up load
      if (token.startsWith('demo-token-')) {
        let role = token.replace('demo-token-', '');
        if (!['admin', 'user', 'doctor'].includes(role)) role = 'user';

        setUser({
          id: role === 'admin' ? 1 : role === 'user' ? 2 : 3,
          email: role === 'admin' ? 'admin@klinikhewan.com' : role === 'doctor' ? 'doctor@petcare.com' : 'user@example.com',
          role: role as any,
          name: role.charAt(0).toUpperCase() + role.slice(1) + ' Demo'
        });
        setLoading(false);
        return;
      }

      api.auth.getCurrentUser()
        .then(response => setUser(response.user))
        .catch(() => {
          localStorage.removeItem('token');
          api.setToken(null); // Clear token from API if invalid
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    try {
      // Check for demo credentials locally first to avoid 401 errors
      if (email === 'admin@klinikhewan.com' && password === 'admin123') {
        const dummyUser: User = { id: 1, email, name: 'Admin User', role: 'admin' };
        setUser(dummyUser);
        const token = 'demo-token-admin';
        localStorage.setItem('token', token);
        api.setToken(token);
        setLoading(false);
        return;
      } else if (email === 'user@example.com' && password === 'user123') {
        const dummyUser: User = { id: 2, email, name: 'Demo User', role: 'user' };
        setUser(dummyUser);
        const token = 'demo-token-user';
        localStorage.setItem('token', token);
        api.setToken(token);
        setLoading(false);
        return;
      } else if (email === 'doctor@petcare.com' && password === 'doctor123') {
        const dummyUser: User = { id: 3, email, name: 'Doctor User', role: 'doctor' };
        setUser(dummyUser);
        const token = 'demo-token-doctor';
        localStorage.setItem('token', token);
        api.setToken(token);
        setLoading(false);
        return;
      }

      const response = await api.auth.login({ email, password });
      api.setToken(response.token);
      setUser(response.user);
    } catch (error) {
      console.error('Login error details:', error);
      throw error;
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const response = await api.auth.register(userData);
      api.setToken(response.token);
      setUser(response.user);
    } catch (error) {
      throw error;
    }
  };

  const logout = () => {
    api.removeToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};